import { css } from '@emotion/react';
import React, { useContext, useState } from 'react';
import { isEqual, uniq } from 'underscore';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import CourseSearchInput from 'shared/components/inputs/course-search-input';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import { AnalyticsContext } from './analytics-dashboard';

interface CohortsSearchDropdownProps {
  initialIds?: number[]
  onUpdateFilter?: (ids: number[]) => void
}

const styles = css`
  .search-input {
    width: 600px;
    border-radius: 5px;
  }
  .autocomplete-results-container {
    inset: 0px auto auto 8px !important;
  }
`;

const CohortsSearchDropdown: React.FC<CohortsSearchDropdownProps> = ({ initialIds, onUpdateFilter }) => {
  const { cohorts } = useContext(AnalyticsContext);

  const cohortIds = cohorts?.map(item => item.id);
  const [compareIds, setCompareIds] = useState<number[]>(initialIds ?? cohortIds ?? []);

  const handleCourseSelect = (id: number) => {
    let ids = [...compareIds];

    if (compareIds.includes(id)) {
      ids = compareIds.filter(item => item !== id);
    } else {
      ids = uniq([...compareIds, id]);
    }
    setCompareIds(ids);
  };

  const fetchPredictions = (hint: string) => {
    if (!hint || !cohorts?.length) return cohorts;
    return cohorts.filter((c) => c.name.toLowerCase().includes(hint.toLowerCase()) || c.catalogId.toLowerCase().includes(hint.toLowerCase()));
  };

  const updateDisabled = isEqual(compareIds, initialIds);

  const SearchResultItem = React.forwardRef<HTMLDivElement, any>((props, ref) => {
    const {
      denied,
      item,
      onSelectItem,
      ...restProps
    } = props;

    return (
      <ClickableContainer
        {...restProps}
        ref={ref}
        className='d-flex align-items-center bg-white py-2 pl-3 pr-2 search-result'
        onClick={() => {
          onSelectItem?.(item);
          handleCourseSelect(item.id);
        }}
        role='checkbox'
        aria-checked={compareIds.includes(item.id)}
        tabIndex={0}
      >
        <NvCheckbox
          name={`cohort_item_${item.id}`}
          tabIndex={-1}
          label={item.name}
          labelClassName='text-regular mr-1 mb-0'
          checked={compareIds.includes(item.id)}
          className='d-flex text-nowrap'
        />
        <div className='text-small font-weight-bold text-gray-2 ellipsis'>
          {item.catalogId}
        </div>
      </ClickableContainer>
    );
  });

  return (
    <div
      css={styles}
      className='d-flex justify-content-center mt-4 align-items-center'
    >
      <CourseSearchInput
        fetchCoursePredictions={fetchPredictions}
        SearchResultItem={SearchResultItem}
        searchInputClassName='search-input d-flex align-items-center bg-white pl-1'
        alwaysShowInput
        placeholder={compareIds.length > 0 ? t.FORM.DROPDOWN.SELECTED(compareIds.length) : t.COHORT_ANALYTICS.FILTER()}
        maxResults={10}
        searchInputExtraContent={(
          <NvIcon
            icon='dropdown-arrow'
            size='xss-smallest'
            className='gray-3 pr-2'
          />
        )}
      />
      <div className='py-1'>
        <Button
          variant={updateDisabled ? 'secondary' : 'primary'}
          className='ml-2'
          onClick={() => onUpdateFilter(compareIds)}
          disabled={updateDisabled}
        >
          {t.FORM.UPDATE()}
        </Button>
      </div>
    </div>
  );
};

export default CohortsSearchDropdown;
